import React, { useState, useEffect } from "react";
import { Modal, Form, Button, Alert, Badge } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { Entry } from "../../redux/entrySlice";
import styles from "./StripeOverrideModal.module.scss";

function StripeOverrideModal({ show, handleClose, cafeId, cafeData, onSuccess }) {
  const [submitting, setSubmitting] = useState(false);
  const dispatch = useDispatch();
  const isCurrentlyExempt = cafeData?.stripeVerificationRequired === false;

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      exempt: isCurrentlyExempt,
      reason: cafeData?.stripeExemptReason || "",
    },
  });

  const exemptValue = watch("exempt");

  useEffect(() => {
    if (show) {
      reset({
        exempt: isCurrentlyExempt,
        reason: cafeData?.stripeExemptReason || "",
      });
    }
  }, [show, isCurrentlyExempt, cafeData, reset]);

  const onSubmit = (formData) => {
    setSubmitting(true);

    const data = {
      actionUrl: `cafe/${cafeId}/override-stripe`,
      actionMethod: "post",
      apiData: {
        exempt: formData.exempt,
        reason: formData.reason,
      },
    };

    dispatch(Entry(data)).then((resp) => {
      setSubmitting(false);
      if (resp.payload.code === 200) {
        toast.success(
          formData.exempt
            ? "Stripe requirement removed successfully!"
            : "Stripe requirement restored successfully!"
        );
        if (onSuccess) {
          onSuccess();
        }
        handleClose();
      } else {
        toast.error(resp.payload.message || "Failed to update Stripe override");
      }
    });
  };

  return (
    <Modal show={show} onHide={handleClose} centered className={styles.stripeModal}>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fa fa-stripe me-2" aria-hidden="true"></i>
          Stripe Verification Override
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="info" className="mb-4">
          <i className="fa fa-info-circle me-2" aria-hidden="true"></i>
          <strong>What is this?</strong> By default, all cafés must complete Stripe
          verification before going live. This override allows special cases (pilot programs,
          alternative payment arrangements) to bypass this requirement.
        </Alert>

        {/* Current Status */}
        <div className={styles.currentStatus}>
          <strong>Current Status:</strong>{" "}
          {isCurrentlyExempt ? (
            <Badge bg="warning">
              <i className="fa fa-exclamation-triangle me-1" aria-hidden="true"></i>
              Stripe NOT Required (Override Active)
            </Badge>
          ) : (
            <Badge bg="success">
              <i className="fa fa-check me-1" aria-hidden="true"></i>
              Stripe Required (Normal Flow)
            </Badge>
          )}
        </div>

        {cafeData?.stripeExemptReason && isCurrentlyExempt && (
          <Alert variant="secondary" className="mt-3">
            <strong>Current Exempt Reason:</strong>
            <p className="mb-0 mt-2">{cafeData.stripeExemptReason}</p>
          </Alert>
        )}

        <Form onSubmit={handleSubmit(onSubmit)} className="mt-4">
          <Form.Group className="mb-4">
            <Form.Check
              type="checkbox"
              id="exemptCheckbox"
              label={
                <span className={styles.checkboxLabel}>
                  <strong>Exempt this café from Stripe verification</strong>
                  <br />
                  <small className="text-muted">
                    When checked, this café can go live without completing Stripe
                  </small>
                </span>
              }
              {...register("exempt")}
              className={styles.exemptCheckbox}
            />
          </Form.Group>

          {exemptValue && (
            <Form.Group className="mb-3">
              <Form.Label>
                Reason for Override <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                placeholder="e.g., Pilot program participant, Alternative payment arrangement with Organization X, Testing phase, etc."
                {...register("reason", {
                  required: exemptValue ? "Please provide a reason for the override" : false,
                  minLength: {
                    value: 10,
                    message: "Please provide at least 10 characters",
                  },
                })}
                isInvalid={!!errors.reason}
              />
              <Form.Control.Feedback type="invalid">
                {errors.reason?.message}
              </Form.Control.Feedback>
              <Form.Text className="text-muted">
                This reason will be logged in the audit trail. Be specific about why this
                override is needed.
              </Form.Text>
            </Form.Group>
          )}

          {exemptValue && cafeData?.cafeStatus === "Approved" && (
            <Alert variant="success" className="mt-3">
              <i className="fa fa-check-circle me-2" aria-hidden="true"></i>
              <strong>Note:</strong> Since this café is already approved, activating this
              override will immediately finalize their onboarding and make them live (if POS
              is connected).
            </Alert>
          )}

          {!exemptValue && isCurrentlyExempt && (
            <Alert variant="warning" className="mt-3">
              <i className="fa fa-exclamation-triangle me-2" aria-hidden="true"></i>
              <strong>Warning:</strong> Removing this override will require the vendor to
              complete Stripe verification before they can accept orders.
            </Alert>
          )}

          <Modal.Footer className="border-0 px-0 pb-0">
            <Button
              variant="secondary"
              onClick={handleClose}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant={exemptValue ? "warning" : "primary"}
              disabled={submitting}
              className={styles.submitButton}
            >
              {submitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Processing...
                </>
              ) : (
                <>
                  <i
                    className={`fa ${exemptValue ? "fa-shield" : "fa-check"} me-2`}
                    aria-hidden="true"
                  ></i>
                  {exemptValue ? "Apply Override" : "Restore Stripe Requirement"}
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal.Body>
    </Modal>
  );
}

export default StripeOverrideModal;
